import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { CopyButton } from "components/CopyButton/CopyButton";
import { SkeletonBlock } from "components/general/Skeleton";
import { Spacer } from "components/general/Spacer/Spacer";
import { Text } from "components/general/Text";
import { NETWORK_NAME_TO_DISPLAY } from "config/consts";
import { FlexBetweenContainer } from "containers/FlexBetweenContainer";
import { useFetchEnsAvatar, useFetchEnsName } from "hooks/queries/ens-queries";
import { useNetworkButtonConfig } from "hooks/useNetworkButtonConfig";
import { capitalize } from "lodash-es";
import { PropsWithChildren, useMemo, useState } from "react";
import { GlobalStore } from "store/global/GlobalStore";
import { TransactionDetail } from "types/history";
import { formatAddress, formatTxHash } from "utils/walletUtils";

interface ActivityDetailDescriptionProps {
  chainId: TransactionDetail["chain_id"];
  fee: TransactionDetail["gasFeeUsd"];
  txHash: TransactionDetail["txHash"];
  sender: TransactionDetail["sender"];
  receiver: TransactionDetail["receiver"];
}

export const ActivityDetailDescription = ({
  chainId,
  fee,
  txHash,
  sender,
  receiver,
}: ActivityDetailDescriptionProps) => {
  const theme = GlobalStore.useState(s => s.theme);

  const networkName = NETWORK_NAME_TO_DISPLAY[chainId];
  const networkConfig = useNetworkButtonConfig();
  const NetworkIcon = networkConfig[chainId].icon;

  const [isTxHashCopied, setIsTxHashCopied] = useState(false);

  return (
    <Wrapper>
      <TradeAndSendDescription sender={sender} receiver={receiver} chainId={chainId} />
      <Row title="Network">
        <NetworkIcon width={16} height={16} />
        <Spacer width="2px" />
        <Text textStyle="Paragraph" size="Medium">
          {capitalize(networkName)}
        </Text>
      </Row>
      <Row title={`Included ${networkName} Fee`}>
        <Text textStyle="Paragraph" size="Medium">
          ${fee.toFixed(2)}
        </Text>
      </Row>
      <Row title="Transaction">
        <CopyButton
          isCopied={isTxHashCopied}
          setIsCopied={setIsTxHashCopied}
          copyValue={txHash}
          iconPlacement="right"
          iconSize={16}
          iconGap={8}
          css={css`
            color: ${theme.button.tertiary.text.default};
            transition: color 250ms linear;

            &:hover {
              color: ${theme.button.tertiary.text.hover};
            }
          `}
        >
          <Text textStyle="Paragraph" size="Medium">
            {formatTxHash(txHash)}
          </Text>
        </CopyButton>
      </Row>
    </Wrapper>
  );
};

// Sub-components

const Wrapper = styled.div`
  padding: 0 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 15px;
`;

const Row = ({ title, children }: PropsWithChildren<{ title: string }>) => {
  const theme = GlobalStore.useState(s => s.theme);
  return (
    <FlexBetweenContainer>
      <Text textStyle="Paragraph" size="Medium" color={theme.font.secondary.default}>
        {title}
      </Text>
      <FlexBetweenContainer>{children}</FlexBetweenContainer>
    </FlexBetweenContainer>
  );
};

const EnsAvatarContainer = styled.img`
  width: 12px;
  height: 12px;
  border-radius: 50px;
  position: relative;
  top: -1px;
`;

interface TradeAndSendDescriptionProps {
  sender: TransactionDetail["sender"];
  receiver: TransactionDetail["receiver"];
  chainId: TransactionDetail["chain_id"];
}

const TradeAndSendDescription = ({ sender, receiver, chainId }: TradeAndSendDescriptionProps) => {
  const address = GlobalStore.useState(s => s.address);
  const isSender = address === sender;
  const title = isSender ? "Sent to" : "Received from";
  const addressToDisplay = isSender ? receiver : sender;

  const { data: ensName, isLoading: ensNameIsLoading } = useFetchEnsName({
    address: addressToDisplay,
    chainId,
  });
  const { data: ensAvatarUrl, isLoading: ensAvatarIsLoading } = useFetchEnsAvatar({
    name: ensName,
    chainId,
  });

  const addressOrEnsName = useMemo(() => {
    if (ensNameIsLoading) {
      return <SkeletonBlock width={74} height={14} />;
    }
    return ensName ?? formatAddress(addressToDisplay);
  }, [addressToDisplay, ensName, ensNameIsLoading]);

  const ensAvatar = useMemo(() => {
    if (ensAvatarIsLoading || ensAvatarUrl == null) return null;
    return (
      <>
        <EnsAvatarContainer src={ensAvatarUrl} width={12} height={12} alt="ENS Avatar" />
        <Spacer width={4} />
      </>
    );
  }, [ensAvatarIsLoading, ensAvatarUrl]);

  if (sender === receiver) return null;

  return (
    <Row title={title}>
      {ensAvatar}
      <Text textStyle="Paragraph" size="Medium">
        {addressOrEnsName}
      </Text>
    </Row>
  );
};
